/**
 * Tests whether at least one user permission matches the given pattern.
 *
 * Supports two matching modes:
 * - **Wildcard:** If `pattern` contains `*`, it is converted to a regex where
 *   `*` matches any characters within a single segment (anything except `:`).
 *   Example: `'identity.users:*'` matches `'identity.users:view'` and
 *   `'identity.users:manage'`, but NOT `'identity.roles:view'`.
 * - **Exact:** If no wildcard is present, an exact string comparison is used.
 *
 * @param pattern          - Permission pattern to check (may contain `*`).
 * @param userPermissions  - Array of resolved permissions from the JWT token.
 * @returns `true` if at least one user permission satisfies the pattern.
 */
export function matchPermissionPattern(pattern: string, userPermissions: string[]): boolean {
  if (!pattern || !userPermissions.length) {
    return false;
  }

  if (!pattern.includes('*')) {
    return userPermissions.includes(pattern);
  }

  // Escape regex-special characters (primarily `.`), then replace `*` with
  // a segment-scoped wildcard that matches anything except the `:` separator.
  const regexSource = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '[^:]*');
  const regex = new RegExp(`^${regexSource}$`);

  return userPermissions.some((permission) => regex.test(permission));
}
